import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import LudoTrianglePlayerBox from "./components/LuduBoard/LudoTrianglePlayerBox";
import LudoStarBoxes from "./components/LuduBoard/LudoStarBoxes";
import LudoMarkerGoti from "./components/LuduBoard/LudoMarkerGoti";
import LudoWinBox from "./components/LuduBoard/LudoWinBox";
import LudoPolygon from "./components/LuduBoard/LudoPolygon";
import LudoBoxes from "./components/LuduBoard/LudoBoxes";
import { generatePolygonCordinates } from "./geometry/polygon";
import { generateLineCordinates } from "./geometry/lines";
import { generateBoxesFromLineCoordinates } from "./geometry/boxes";
import { generateDropdownMarker } from "./geometry/markers";
import { generateTriangleCordForPlayerBox } from "./geometry/triangles";
import { generateInnerTriangleCordForPlayerBox } from "./geometry/innerTriangle";
import { generateWinBoxCord } from "./geometry/winbox";
import { generateCircleCordForPlayer } from "./geometry/circle";
import { generateHomeDropdownMarker } from "./geometry/homeMarkers";
import { playSound, startLoopSound, stopLoopSound, preloadGameplaySounds } from "./soundEffects";
import { playVoice, setVoiceReactionsEnabled } from "./voiceReactions";
import SpeedTimer from "./components/SpeedTimer";
import TeamScore from "./components/TeamScore";
import MemeCard from "./components/MemeCard";
import SpectatorPanel from "./components/SpectatorPanel";

const Dice3D = React.lazy(() => import("./components/LuduBoard/Dice3D"));

// Constants
const POLYGON_SIZE = 80;

/* ================= DYNAMIC HELPERS ================= */

function generatePlayers(playerCount, totalCells) {
  return Array.from({ length: playerCount }, (_, i) => ({
    id: i,
    start: i === 0 ? totalCells - 4 : i * 18 - 4,
    isAI: true,
  }));
}

function generateSafeCells(players) {
  // The starting positions are the safe cells.
  const res = [];
  players.forEach((p) =>
    res.push(p.start - 1, (p.start + 7) % (18 * players.length)),
  );
  return res;
}

function createInitialGotis(players) {
  const gotis = [];
  players.forEach((p) => {
    for (let i = 0; i < 4; i++) {
      gotis.push({
        id: `${p.id}-${i}`,
        playerId: p.id,
        position: -1, // home
        finished: false,
      });
    }
  });
  return gotis;
}

const SPEED_SECONDS = 15;
const TEAM_REACTION_EMOJIS = ["🔥", "👍", "🛡️", "😬"];

function LudoBoard({
  playerCount,
  SVG_SIZE,
  online = false,
  enableBots = false,
  localPlayerId = null,
  remoteGameState = null,
  remoteAction = null,
  onRollDice,
  onMoveGoti,
  onGameStateChange,
  onTurnChange,
  onPointsChange = null,
  speedMode = false,
  teamMode = false,
  playerNames = [],
  spectatorMode = false,
  spectatorCount = 0,
  spectatorReactions = [],
  onSpectatorReaction = null,
  autoTurnEnabled = {},
  onPlayAgain = null,
  onExitHome = null,
}) {
  const TOTAL_CELLS = playerCount * 18;
  const [isRolling, setIsRolling] = useState(false);
  const [diceRolling, setDiceRolling] = useState(false);
  const [animatedDice, setAnimatedDice] = useState(1);
  const [captureRequest, setCaptureRequest] = useState(null);
  const [finishRequest, setFinishRequest] = useState(null);
  const [showLegendary, setShowLegendary] = useState(false);
  const [timeLeft, setTimeLeft] = useState(SPEED_SECONDS);
  const [teamWinner, setTeamWinner] = useState(null); // "A" | "B" | null
  const [reactions, setReactions] = useState([]);     // [{ id, emoji, x }]
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [memeStats, setMemeStats] = useState(null);
  const [showMemeCard, setShowMemeCard] = useState(false);
  const [safeCellEffect, setSafeCellEffect] = useState(null); // { position, key }
  const gameStartTimeRef = useRef(Date.now());
  // Kick off gameplay sound preload as soon as the board mounts
  useEffect(() => { preloadGameplaySounds(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const captureLogRef = useRef([]);      // [{ attacker, victim }]
  const legendaryPlayerRef = useRef(null);
  const memeComputedRef = useRef(false);
  const pendingGameUpdateRef = useRef(null);
  const pendingFinishUpdateRef = useRef(null);
  const botDiceAnimatingRef = useRef(false);
  const consecutiveSixesRef = useRef(0);
  const lastSixPlayerRef = useRef(null);
  const legendaryTimerRef = useRef(null);
  const speedAutoFiredRef = useRef(false);
  const autoTurnDiceFiredRef = useRef(false);
  const gotiStepsRef = useRef({});       // gotiId → accumulated step count
  const [playerPoints, setPlayerPoints] = useState({});
  const [finishBonuses, setFinishBonuses] = useState([]); // floating +50 toasts

  const PLAYERS = useMemo(
    () => generatePlayers(playerCount, TOTAL_CELLS),
    [playerCount, TOTAL_CELLS],
  );

  const SAFE_CELLS = useMemo(() => generateSafeCells(PLAYERS), [PLAYERS]);

  /* ---------- GAME STATE ---------- */

  const [game, setGame] = useState(() => ({
    currentPlayer: 0,
    dice: null,
    gotis: createInitialGotis(PLAYERS),
    rankings: [],
    winner: null,
  }));

  const [moveRequest, setMoveRequest] = useState(null);
  const lastDiceRef = useRef(null);
  const [rankingAnnouncement, setRankingAnnouncement] = useState(null);
  const lastWinnerRef = useRef(null);
  const prevRankingsLenRef = useRef(0);
  const skipNextDiceSoundRef = useRef(false);
  const skipNextMoveStartSoundRef = useRef(false);

  const isHumanTurn = useCallback(
    (playerId = game.currentPlayer) => {
      if (online) return localPlayerId === playerId;
      if (enableBots) return localPlayerId === playerId;
      return true;
    },
    [game.currentPlayer, online, enableBots, localPlayerId],
  );

  const calculateMove = useCallback(
    (goti, dice) => {
      const LADDER_JUMP = 6;
      const isLadderCell = (pos) => pos % 18 === 6;

      const player = PLAYERS.find((p) => p.id === goti.playerId);

      const hasOpponent = (pos) =>
        !SAFE_CELLS.includes(pos) &&
        game.gotis.some(
          (g) => g.position === pos && g.playerId !== goti.playerId && !g.finished,
        );

      if (goti.position === -1) {
        const path = [];
        let currentPos = player.start - 1;

        for (let i = 0; i < dice; i++) {
          currentPos = (currentPos + 1) % TOTAL_CELLS;
          path.push(currentPos);
        }

        const newPosition = path[path.length - 1];

        return {
          path,
          newPosition,
          finished: false,
          ate: hasOpponent(newPosition),
        };
      }

      const path = [];
      let currentPos = goti.position;
      const canEnterHome =
        player.start - currentPos <= 12 && player.start - currentPos > 0;

      if (canEnterHome) {
        if (currentPos + dice >= player.start - 1) return null;
        const finished = currentPos + dice === player.start - 2;
        for (let i = 0; i < dice; i++) {
          currentPos = (currentPos + 1) % TOTAL_CELLS;
          path.push(currentPos);
        }
        const newPosition = goti.position + dice;
        return { path, newPosition, finished, ate: !finished && hasOpponent(newPosition) };
      }

      for (let i = 0; i < dice; i++) {
        // Suppress ladders inside this player's own home-lane cells.
        // canEnterHome only checks the goti's starting position, so a goti
        // just outside the home lane (start-13) can still traverse home-lane
        // cells during its path. The ladder at start-8 would otherwise shoot
        // the goti straight to start-2 (win cell) through the outer-board
        // branch, bypassing the home lane entirely.
        const distToStart = player.start - currentPos;
        const inOwnHomeLane = distToStart > 0 && distToStart <= 12;
        if (!inOwnHomeLane && isLadderCell(currentPos)) {
          currentPos = (currentPos + LADDER_JUMP) % TOTAL_CELLS;
          path.push(currentPos);
        } else {
          currentPos = (currentPos + 1) % TOTAL_CELLS;
          path.push(currentPos);
        }
      }

      const newPosition = path[path.length - 1];
      // A goti can ONLY finish through the canEnterHome branch above.
      // Never mark finished here — the outer board never leads directly to
      // the win cell; that path always goes through the home lane.
      return {
        path,
        newPosition,
        finished: false,
        ate: hasOpponent(newPosition),
      };
    },
    [game.gotis, PLAYERS, SAFE_CELLS, TOTAL_CELLS],
  );

  const requestMove = useCallback(
    (gotiId, playerId = game.currentPlayer, options = {}) => {
      const rejectMove = () => {
        if (options.feedback) playSound("blocked");
        return false;
      };

      // 1. Check if dice is rolled, not animating, and no winner
      if (!game.dice || isRolling || diceRolling || game.winner !== null)
        return rejectMove();
      if (online && playerId !== game.currentPlayer) return rejectMove();
      if (enableBots && !isHumanTurn(playerId)) {
        if (playerId === localPlayerId) return rejectMove();
      }

      // 2. Find the goti
      const goti = game.gotis.find((g) => g.id === gotiId);
      if (!goti || goti.playerId !== playerId || goti.finished)
        return rejectMove();

      // 3. Calculate the move
      const move = calculateMove(goti, game.dice);
      if (!move) return rejectMove();
      const openedFromHome = goti.position === -1;

      if (options.sound !== false) {
        playSound(openedFromHome ? "open" : "select");
      }

      // 4. Set up the animation request
      setIsRolling(true);
      setMoveRequest({
        gotiId,
        steps: game.dice,
        openedFromHome,
        ...move,
      });
      return true;
    },
    [
      game.currentPlayer,
      game.dice,
      game.gotis,
      game.winner,
      isRolling,
      online,
      enableBots,
      isHumanTurn,
      localPlayerId,
      calculateMove,
      setIsRolling,
      setMoveRequest,
      diceRolling,
    ],
  );

  // Smart goti selector for auto-turn: eat > furthest-on-board > open from home
  const pickBestGoti = useCallback(
    (gotis, dice, currentPlayerId) => {
      const player = PLAYERS.find((p) => p.id === currentPlayerId);
      const movable = gotis
        .filter((g) => g.playerId === currentPlayerId && !g.finished)
        .map((g) => {
          const move = calculateMove(g, dice);
          return move ? { goti: g, move } : null;
        })
        .filter(Boolean);

      if (!movable.length) return null;

      // Priority 1: capture — prefer eating the opponent with most accumulated steps
      const capturing = movable.filter((m) => m.move.ate);
      if (capturing.length) {
        return capturing.reduce((best, cur) => {
          const victimSteps = (pos) => {
            const v = gotis.find(
              (g) => g.position === pos && g.playerId !== currentPlayerId && !g.finished,
            );
            return v ? (gotiStepsRef.current[v.id] || 0) : 0;
          };
          return victimSteps(cur.move.newPosition) > victimSteps(best.move.newPosition)
            ? cur
            : best;
        }).goti;
      }

      // Priority 2: board goti furthest along its path
      const onBoard = movable.filter((m) => m.goti.position >= 0);
      if (onBoard.length) {
        return onBoard.reduce((best, cur) => {
          const progress = (g) =>
            (g.position - (player.start - 1) + TOTAL_CELLS) % TOTAL_CELLS;
          return progress(cur.goti) > progress(best.goti) ? cur : best;
        }).goti;
      }

      // Priority 3: open from home
      return movable[0].goti;
    },
    [calculateMove, PLAYERS, TOTAL_CELLS],
  );

  useEffect(() => {
    if (remoteGameState) {
      setGame(remoteGameState);
      setMoveRequest(null);
      setIsRolling(false);
    }
  }, [remoteGameState]);

  useEffect(() => {
    if (lastDiceRef.current === null && game.dice !== null) {
      if (skipNextDiceSoundRef.current) {
        skipNextDiceSoundRef.current = false;
      } else {
        playSound("dice");
      }

      // Voice reaction — only for the local player's roll
      const isLocalRoll = online || enableBots
        ? game.currentPlayer === localPlayerId
        : true;
      if (isLocalRoll) {
        if (game.dice === 6) {
          playVoice("six", 220);
        } else if (game.dice <= 2) {
          playVoice("bad_roll", 220);
        }
      }

      // Track consecutive 6s for LEGENDARY ROLL
      if (game.dice === 6 && lastSixPlayerRef.current === game.currentPlayer) {
        consecutiveSixesRef.current += 1;
      } else if (game.dice === 6) {
        consecutiveSixesRef.current = 1;
        lastSixPlayerRef.current = game.currentPlayer;
      } else {
        consecutiveSixesRef.current = 0;
        lastSixPlayerRef.current = null;
      }

      if (consecutiveSixesRef.current >= 3) {
        legendaryPlayerRef.current = game.currentPlayer;
        consecutiveSixesRef.current = 0;
        lastSixPlayerRef.current = null;
        clearTimeout(legendaryTimerRef.current);
        setShowLegendary(true);
        legendaryTimerRef.current = setTimeout(() => setShowLegendary(false), 3000);
        if (isLocalRoll) playVoice("legendary", 550);
      }
    }

    lastDiceRef.current = game.dice;
  }, [game.dice, game.currentPlayer]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (lastWinnerRef.current !== game.winner && game.winner !== null) {
      playSound("win");
      if (game.winner === localPlayerId || (!online && !enableBots)) {
        playVoice("win", 1400);
      }
    }

    lastWinnerRef.current = game.winner;
  }, [game.winner]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show ranking popup when a new player completes all their gotis
  useEffect(() => {
    const rankings = game.rankings ?? [];
    if (rankings.length > prevRankingsLenRef.current) {
      const newPlayerId = rankings[rankings.length - 1];
      const rank = rankings.length;
      setRankingAnnouncement({ playerId: newPlayerId, rank });
      const timer = setTimeout(() => setRankingAnnouncement(null), 5000);
      prevRankingsLenRef.current = rankings.length;
      return () => clearTimeout(timer);
    }
    prevRankingsLenRef.current = rankings.length;
  }, [game.rankings]);

  useEffect(() => {
    if (!remoteAction) return;

    if (remoteAction.type === "roll") {
      setGame((prev) => {
        if (
          prev.winner !== null ||
          prev.dice ||
          prev.currentPlayer !== remoteAction.playerId
        ) {
          return prev;
        }

        const next = { ...prev, dice: remoteAction.dice };
        if (remoteAction.playerId === localPlayerId) {
          window.setTimeout(() => onGameStateChange?.(next), 0);
        }
        return next;
      });
      return;
    }

    if (remoteAction.type === "move") {
      const skipStartSound =
        remoteAction.playerId === localPlayerId &&
        skipNextMoveStartSoundRef.current;

      skipNextMoveStartSoundRef.current = false;
      requestMove(remoteAction.gotiId, remoteAction.playerId, {
        sound: !skipStartSound,
      });
    }
  }, [remoteAction, localPlayerId, requestMove, onGameStateChange]);

  // Bot dice animation — separate effect so setDiceRolling doesn't cause re-runs here
  useEffect(() => {
    if (!enableBots || online || game.winner !== null || isRolling) return;
    if (game.currentPlayer === localPlayerId) return;
    if (game.dice) return;
    if (botDiceAnimatingRef.current) return;

    let cleaned = false;
    let flickerInterval;
    botDiceAnimatingRef.current = true;

    const outerTimer = setTimeout(() => {
      if (cleaned) return;
      const diceValue = Math.floor(Math.random() * 6) + 1;

      setDiceRolling(true);
      playSound("dice");

      flickerInterval = setInterval(() => {
        setAnimatedDice(Math.floor(Math.random() * 6) + 1);
      }, 75);

      setTimeout(() => {
        if (cleaned) return;
        clearInterval(flickerInterval);
        setAnimatedDice(diceValue);
        setDiceRolling(false);
        botDiceAnimatingRef.current = false;
        skipNextDiceSoundRef.current = true;
        setGame((prev) => {
          if (prev.winner !== null || prev.dice || prev.currentPlayer === localPlayerId) {
            return prev;
          }
          return { ...prev, dice: diceValue };
        });
      }, 900);
    }, 700);

    return () => {
      cleaned = true;
      botDiceAnimatingRef.current = false;
      clearTimeout(outerTimer);
      clearInterval(flickerInterval);
    };
  }, [
    enableBots,
    online,
    game.currentPlayer,
    game.dice,
    game.winner,
    isRolling,
    localPlayerId,
  ]);

  // Bot goti movement — runs once dice is set
  useEffect(() => {
    if (!enableBots || online || game.winner !== null || isRolling) return;
    if (game.currentPlayer === localPlayerId) return;
    if (!game.dice) return;

    const botGotis = game.gotis.filter(
      (g) => g.playerId === game.currentPlayer && !g.finished,
    );
    const movableGoti =
      botGotis.find((g) => g.position >= 0 && calculateMove(g, game.dice)) ||
      botGotis.find((g) => calculateMove(g, game.dice));

    if (!movableGoti) return;

    const timer = setTimeout(() => {
      requestMove(movableGoti.id, game.currentPlayer);
    }, 650);

    return () => clearTimeout(timer);
  }, [
    enableBots,
    online,
    game.currentPlayer,
    game.dice,
    game.gotis,
    game.winner,
    isRolling,
    localPlayerId,
    requestMove,
    calculateMove,
  ]);

  // Auto-pass: skip ranked players, pass if no moves possible
  useEffect(() => {
    if (game.winner !== null) return;

    const rankings = game.rankings ?? [];

    // Safety-net: if current player already ranked, skip them immediately
    if (rankings.includes(game.currentPlayer)) {
      const timer = setTimeout(() => {
        setGame((prev) => {
          const r = prev.rankings ?? [];
          let next = (prev.currentPlayer + 1) % PLAYERS.length;
          let s = 0;
          while (r.includes(next) && s < PLAYERS.length) {
            next = (next + 1) % PLAYERS.length;
            s++;
          }
          return { ...prev, dice: null, currentPlayer: next };
        });
      }, 400);
      return () => clearTimeout(timer);
    }

    if (game.dice && !isRolling && !diceRolling) {
      const playerGotis = game.gotis.filter(
        (g) => g.playerId === game.currentPlayer,
      );
      const canMove = playerGotis.some((g) => {
        if (g.finished) return false;
        const move = calculateMove(g, game.dice);
        return move !== null;
      });

      if (!canMove) {
        const timer = setTimeout(() => {
          setGame((prev) => {
            const r = prev.rankings ?? [];
            let next = (prev.currentPlayer + 1) % PLAYERS.length;
            let s = 0;
            while (r.includes(next) && s < PLAYERS.length) {
              next = (next + 1) % PLAYERS.length;
              s++;
            }
            const nextState = { ...prev, dice: null, currentPlayer: next };
            // Keep server turnState in sync when local player auto-passes
            if (online && prev.currentPlayer === localPlayerId) {
              window.setTimeout(() => onGameStateChange?.(nextState), 0);
            }
            return nextState;
          });
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [
    game.dice,
    game.currentPlayer,
    game.gotis,
    game.rankings,
    isRolling,
    PLAYERS.length,
    game.winner,
    calculateMove,
    diceRolling,
    online,
    localPlayerId,
    onGameStateChange,
  ]);

  const onCaptureComplete = useCallback(() => {
    pendingGameUpdateRef.current?.();
    pendingGameUpdateRef.current = null;
    setCaptureRequest(null);
  }, []);

  const onFinishComplete = useCallback(() => {
    pendingFinishUpdateRef.current?.();
    pendingFinishUpdateRef.current = null;
    setFinishRequest(null);
  }, []);

  function onMoveComplete(gotiId /*, finalBox is ignored */) {
    if (!moveRequest || moveRequest.gotiId !== gotiId) return;

    // ── POINTS ──
    const movingPlayer = game.currentPlayer;
    const stepsGained  = moveRequest.steps;
    gotiStepsRef.current[gotiId] = (gotiStepsRef.current[gotiId] || 0) + stepsGained;

    // Pre-compute eat bonus while game.gotis is still current
    let eatBonus    = 0;
    let eatVictimId = null;
    if (moveRequest.ate) {
      const victimGoti = game.gotis.find(
        (g) => g.position === moveRequest.newPosition && g.id !== gotiId && g.playerId !== game.currentPlayer,
      );
      if (victimGoti) {
        eatBonus    = gotiStepsRef.current[victimGoti.id] || 0;
        eatVictimId = victimGoti.playerId;
        gotiStepsRef.current[victimGoti.id] = 0;
      }
    }

    const FINISH_BONUS = 50;
    const didFinish = Boolean(moveRequest.finished);

    setPlayerPoints((prev) => {
      const next = { ...prev };
      next[movingPlayer] = (next[movingPlayer] || 0) + stepsGained + eatBonus + (didFinish ? FINISH_BONUS : 0);
      if (eatVictimId !== null && eatBonus > 0) {
        next[eatVictimId] = Math.max(0, (next[eatVictimId] || 0) - eatBonus);
      }
      return next;
    });

    if (didFinish) {
      const bonusId = Date.now() + Math.random();
      setFinishBonuses((prev) => [...prev, { id: bonusId, playerId: movingPlayer }]);
      setTimeout(() => setFinishBonuses((prev) => prev.filter((b) => b.id !== bonusId)), 1600);
    }

    const applyGameUpdate = () => {
      setGame((prev) => {
        const { newPosition, finished, ate } = moveRequest;

        // 1️⃣ Update goti that moved
        const finishOrder = finished
          ? prev.gotis.filter(
              (g) => g.finished && g.playerId === prev.currentPlayer,
            ).length + 1
          : undefined;

        let gotis = prev.gotis.map((g) =>
          g.id === gotiId
            ? { ...g, position: newPosition, finished, ...(finished && { finishOrder }) }
            : g,
        );

        // 2️⃣ Handle EAT (if it happened)
        if (ate) {
          gotis = gotis.map((g) => {
            if (
              g.position === newPosition &&
              g.id !== gotiId &&
              g.playerId !== prev.currentPlayer
            ) {
              return { ...g, position: -1 };
            }
            return g;
          });
        }

        // 3️⃣ Check if current player completed all gotis → push to rankings
        let rankings = prev.rankings ?? [];
        let winner = prev.winner;
        let justCompletedAll = false;

        if (finished) {
          const playerGotis = gotis.filter((g) => g.playerId === prev.currentPlayer);
          if (playerGotis.every((g) => g.finished) && !rankings.includes(prev.currentPlayer)) {
            justCompletedAll = true;
            rankings = [...rankings, prev.currentPlayer];
            // Game ends when only 1 player remains unfinished
            if (rankings.length >= PLAYERS.length - 1) {
              winner = rankings[0]; // champion = first finisher
              // Add the last unranked player so every player has a finish position
              const lastPlayer = PLAYERS.findIndex((_, i) => !rankings.includes(i));
              if (lastPlayer !== -1) rankings = [...rankings, lastPlayer];
            }
          }
        }

        // 4️⃣ Change turn — no bonus turn for just-completing-all; skip already-ranked players
        const rolledSix = prev.dice === 6;
        const shouldKeepTurn = !justCompletedAll && (rolledSix || ate || finished);

        let nextPlayer = prev.currentPlayer;
        if (!shouldKeepTurn) {
          nextPlayer = (prev.currentPlayer + 1) % PLAYERS.length;
          let safety = 0;
          while (rankings.includes(nextPlayer) && safety < PLAYERS.length) {
            nextPlayer = (nextPlayer + 1) % PLAYERS.length;
            safety++;
          }
        }

        const nextGame = {
          ...prev,
          gotis,
          rankings,
          dice: winner !== null ? prev.dice : null,
          currentPlayer: winner !== null ? prev.currentPlayer : nextPlayer,
          winner,
        };

        if (!online || prev.currentPlayer === localPlayerId) {
          window.setTimeout(() => onGameStateChange?.(nextGame), 0);
        }

        return nextGame;
      });

      setMoveRequest(null);
      setIsRolling(false);
    };

    if (moveRequest.ate) {
      const eatenGoti = game.gotis.find(
        (g) =>
          g.position === moveRequest.newPosition &&
          g.id !== gotiId &&
          g.playerId !== game.currentPlayer,
      );
      playSound("capture");
      if (eatenGoti) {
        // Record capture for meme stats
        captureLogRef.current.push({ attacker: game.currentPlayer, victim: eatenGoti.playerId });
        // Voice: attacker = local → "eat"; victim = local → "eaten"
        if (game.currentPlayer === localPlayerId) {
          playVoice("eat", 320);
        } else if (eatenGoti.playerId === localPlayerId) {
          playVoice("eaten", 180);
        }
        // Defer game update until capture animation finishes
        setCaptureRequest({ gotiId: eatenGoti.id, fromCell: moveRequest.newPosition, attackerGotiId: moveRequest.gotiId });
        pendingGameUpdateRef.current = applyGameUpdate;
        return;
      }
    } else if (moveRequest.finished) {
      playSound("finish");
      setFinishRequest({ gotiId: gotiId, fromCell: moveRequest.newPosition });
      pendingFinishUpdateRef.current = applyGameUpdate;
      return;
    } else if (!moveRequest.openedFromHome) {
      playSound("land");
    }

    // Safe cell / stack protection effect (normal moves only)
    if (!moveRequest.ate && !moveRequest.finished && moveRequest.newPosition >= 0) {
      const pos = moveRequest.newPosition;
      const isSafe = SAFE_CELLS.includes(pos);
      const isNewStack = game.gotis.some(
        (g) => g.id !== gotiId && g.playerId === game.currentPlayer && g.position === pos,
      );
      if (isSafe || isNewStack) {
        playSound("safe_cell");
        setSafeCellEffect({ position: pos, key: Date.now() });
      }
    }

    applyGameUpdate();
  }

  function handleAnimation(gotiId) {
    if (spectatorMode) return;
    if (online) {
      if (!game.dice || isRolling || game.winner !== null) {
        playSound("blocked");
        return;
      }
      if (localPlayerId !== game.currentPlayer) {
        playSound("blocked");
        return;
      }
      const goti = game.gotis.find((g) => g.id === gotiId);
      if (!goti || goti.playerId !== localPlayerId) {
        playSound("blocked");
        return;
      }
      if (!calculateMove(goti, game.dice)) {
        playSound("blocked");
        return;
      }
      skipNextMoveStartSoundRef.current = true;
      playSound(goti.position === -1 ? "open" : "select");
      onMoveGoti?.(gotiId);
      window.setTimeout(() => {
        skipNextMoveStartSoundRef.current = false;
      }, 1500);
      return;
    }

    if (enableBots && !isHumanTurn()) {
      playSound("blocked");
      return;
    }

    requestMove(gotiId, game.currentPlayer, { feedback: true });
  }

  /* ================= COLORS ================= */

  const numberWiseColor = useMemo(() => {
    return Array.from({ length: playerCount }, (_, i) => {
      const hue = (i * 360) / playerCount;
      return `hsl(${hue}, 70%, 50%)`;
    });
  }, [playerCount]);

  useEffect(() => {
    onTurnChange?.({
      currentPlayer: game.currentPlayer,
      colors: numberWiseColor,
    });
  }, [game.currentPlayer, numberWiseColor, onTurnChange]);

  /* ================= GEOMETRY ================= */

  const polygonData = useMemo(
    () =>
      generatePolygonCordinates(
        SVG_SIZE / 2,
        SVG_SIZE / 2,
        playerCount,
        POLYGON_SIZE,
      ),
    [SVG_SIZE, playerCount],
  );

  const polygonDataSmall = useMemo(() => {
    return generatePolygonCordinates(
      SVG_SIZE / 2,
      SVG_SIZE / 2,
      playerCount,
      POLYGON_SIZE / 1.7,
    );
  }, [playerCount, SVG_SIZE]);

  const lineCoordinates = useMemo(() => {
    return generateLineCordinates(polygonData, playerCount);
  }, [polygonData, playerCount]);

  const boxes = useMemo(() => {
    return generateBoxesFromLineCoordinates(lineCoordinates);
  }, [lineCoordinates]);

  const markers = useMemo(() => {
    return generateDropdownMarker(boxes);
  }, [boxes]);

  const [triangleCoordsArray, triangleCoordsStringArray] = useMemo(() => {
    return generateTriangleCordForPlayerBox(lineCoordinates);
  }, [lineCoordinates]);

  const triangleInnerCordsArray = useMemo(() => {
    return generateInnerTriangleCordForPlayerBox(triangleCoordsArray);
  }, [triangleCoordsArray]);

  const circleCoordinates = useMemo(() => {
    return generateCircleCordForPlayer(triangleCoordsArray);
  }, [triangleCoordsArray]);

  const homeMarkers = useMemo(() => {
    return generateHomeDropdownMarker(circleCoordinates);
  }, [circleCoordinates]);

  const winBoxCordLine = useMemo(() => {
    return generateWinBoxCord(polygonData, polygonDataSmall);
  }, [polygonData, polygonDataSmall]);

  // const starCords = useMemo(() => {
  //   return generateStarPolygon(boxes, playerCount);
  // }, [boxes, playerCount]);

  const diceDisplayValue = diceRolling ? animatedDice : game.dice;

  const diceButtonDisabled = Boolean(
    spectatorMode || isRolling || diceRolling || game.dice || game.winner !== null,
  );

  const handleDiceClick = useCallback(() => {
    if (diceButtonDisabled) { playSound("blocked"); return; }
    if (online && localPlayerId !== game.currentPlayer) { playSound("blocked"); return; }
    if (enableBots && !isHumanTurn()) { playSound("blocked"); return; }

    const diceValue = Math.floor(Math.random() * 6) + 1;
    setDiceRolling(true);
    playSound("dice");

    setTimeout(() => {
      setAnimatedDice(diceValue);
      setDiceRolling(false);
      if (online) {
        skipNextDiceSoundRef.current = true;
        onRollDice?.(diceValue);
        window.setTimeout(() => { skipNextDiceSoundRef.current = false; }, 1500);
      } else {
        skipNextDiceSoundRef.current = true;
        setGame((prev) => ({ ...prev, dice: diceValue }));
      }
    }, 900);
  }, [diceButtonDisabled, online, localPlayerId, game.currentPlayer, enableBots, isHumanTurn, onRollDice]); // eslint-disable-line react-hooks/exhaustive-deps

  const movableGotis = useMemo(() =>
    game.gotis
      .filter((g) => g.playerId === game.currentPlayer && !g.finished)
      .filter((g) => (game.dice ? calculateMove(g, game.dice) : false))
      .map((g) => g.id),
  [game.gotis, game.currentPlayer, game.dice, calculateMove]); // eslint-disable-line react-hooks/exhaustive-deps

  const localId = online || enableBots ? localPlayerId : game.currentPlayer;
  const eatHints = useMemo(() => {
    if (!game.dice || moveRequest || game.currentPlayer !== localId) return [];
    return game.gotis
      .filter((g) => g.playerId === game.currentPlayer && !g.finished && g.position >= 0)
      .reduce((acc, g) => {
        const move = calculateMove(g, game.dice);
        if (move?.ate) acc.push({ fromPos: g.position, toPos: move.newPosition });
        return acc;
      }, []);
  }, [game.dice, game.gotis, game.currentPlayer, calculateMove, moveRequest, localId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (eatHints.length > 0) {
      startLoopSound("eat_indicator");
    } else {
      stopLoopSound("eat_indicator");
    }
    return () => stopLoopSound("eat_indicator");
  }, [eatHints.length]);

  // Speed Ludo: reset timer whenever the current player changes
  useEffect(() => {
    setTimeLeft(SPEED_SECONDS);
    speedAutoFiredRef.current = false;
    autoTurnDiceFiredRef.current = false;
  }, [speedMode, game.currentPlayer]);

  // Speed Ludo: count down only when it is the human player's turn
  useEffect(() => {
    if (!speedMode || game.winner !== null || isRolling || diceRolling) return;
    if (!isHumanTurn()) return;

    const id = setInterval(() => {
      setTimeLeft((t) => Math.max(0, t - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [speedMode, game.winner, isRolling, diceRolling, game.currentPlayer, isHumanTurn]);

  // Speed Ludo: fire auto-action once when timer hits 0
  useEffect(() => {
    if (!speedMode || timeLeft > 0 || game.winner !== null) return;
    if (!isHumanTurn() || isRolling || diceRolling) return;
    if (speedAutoFiredRef.current) return;
    speedAutoFiredRef.current = true;

    if (!game.dice) {
      // Auto-roll with quick animation
      const diceValue = Math.floor(Math.random() * 6) + 1;
      setDiceRolling(true);
      playSound("dice");
      const fi = setInterval(() => setAnimatedDice(Math.floor(Math.random() * 6) + 1), 75);
      setTimeout(() => {
        clearInterval(fi);
        setAnimatedDice(diceValue);
        setDiceRolling(false);
        skipNextDiceSoundRef.current = true;
        setGame((prev) => {
          if (prev.winner !== null || prev.dice) return prev;
          return { ...prev, dice: diceValue };
        });
      }, 500);
    } else {
      // Auto-move best available goti
      const playerGotis = game.gotis.filter(
        (g) => g.playerId === game.currentPlayer && !g.finished,
      );
      const movable =
        playerGotis.find((g) => g.position >= 0 && calculateMove(g, game.dice)) ||
        playerGotis.find((g) => calculateMove(g, game.dice));
      if (movable) requestMove(movable.id, game.currentPlayer);
    }
  }, [timeLeft]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-turn: auto-roll dice when it's an auto-turn player's turn.
  // Mirrors the bot dice effect pattern: NO diceRolling dep so cleanup never
  // fires mid-animation and cancels the flicker interval.
  useEffect(() => {
    const isAutoPlayer = autoTurnEnabled[game.currentPlayer];
    if (!isAutoPlayer) return;
    if (game.winner !== null || isRolling || game.dice) return;
    if (online && localPlayerId !== game.currentPlayer) return;
    if (enableBots && game.currentPlayer !== localPlayerId) return;
    if (autoTurnDiceFiredRef.current) return;

    let cleaned = false;
    let fi;
    autoTurnDiceFiredRef.current = true;
    const diceValue = Math.floor(Math.random() * 6) + 1;

    const outerTimer = setTimeout(() => {
      if (cleaned) return;
      setDiceRolling(true);
      playSound("dice");
      fi = setInterval(() => setAnimatedDice(Math.floor(Math.random() * 6) + 1), 75);

      setTimeout(() => {
        if (cleaned) return;
        clearInterval(fi);
        setAnimatedDice(diceValue);
        setDiceRolling(false);
        // Reset ref HERE so same-player re-rolls (after a 6 or eat) work
        autoTurnDiceFiredRef.current = false;
        skipNextDiceSoundRef.current = true;

        if (online) {
          onRollDice?.(diceValue);
          window.setTimeout(() => { skipNextDiceSoundRef.current = false; }, 1500);
        } else {
          setGame((prev) => {
            if (prev.winner !== null || prev.dice) return prev;
            return { ...prev, dice: diceValue };
          });
        }
      }, 900);
    }, 500);

    return () => {
      cleaned = true;
      autoTurnDiceFiredRef.current = false;
      clearTimeout(outerTimer);
      clearInterval(fi);
    };
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    game.currentPlayer, game.dice, game.winner,
    isRolling, autoTurnEnabled,
    online, enableBots, localPlayerId, onRollDice,
  ]);

  // Auto-turn: auto-move best goti once dice is rolled.
  // No diceRolling dep — dice is set in the same React batch as diceRolling=false,
  // so game.dice being non-null already implies the animation is done.
  useEffect(() => {
    const isAutoPlayer = autoTurnEnabled[game.currentPlayer];
    if (!isAutoPlayer) return;
    if (!game.dice || game.winner !== null || isRolling) return;
    if (online && localPlayerId !== game.currentPlayer) return;
    if (enableBots && game.currentPlayer !== localPlayerId) return;

    const bestGoti = pickBestGoti(game.gotis, game.dice, game.currentPlayer);
    if (!bestGoti) return;

    let cleaned = false;
    const timer = setTimeout(() => {
      if (cleaned) return;
      if (online) {
        skipNextMoveStartSoundRef.current = true;
        playSound(bestGoti.position === -1 ? "open" : "select");
        onMoveGoti?.(bestGoti.id);
        window.setTimeout(() => { skipNextMoveStartSoundRef.current = false; }, 1500);
      } else {
        requestMove(bestGoti.id, game.currentPlayer);
      }
    }, 500);

    return () => { cleaned = true; clearTimeout(timer); };
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    game.currentPlayer, game.dice, game.gotis, game.winner,
    isRolling, autoTurnEnabled,
    online, enableBots, localPlayerId,
    onMoveGoti, pickBestGoti, requestMove,
  ]);

  // Team mode: detect when an entire team (both partners) finishes
  useEffect(() => {
    if (!teamMode || teamWinner) return;
    const rankings = game.rankings ?? [];
    const aIds = PLAYERS.filter((p) => p.id % 2 === 0).map((p) => p.id);
    const bIds = PLAYERS.filter((p) => p.id % 2 !== 0).map((p) => p.id);
    const aWon = aIds.every((id) => rankings.includes(id));
    const bWon = bIds.every((id) => rankings.includes(id));
    const localOnWinningTeam =
      (aWon && localPlayerId % 2 === 0) || (bWon && localPlayerId % 2 !== 0);
    if (aWon) { setTeamWinner("A"); playSound("win"); if (localOnWinningTeam) playVoice("win", 1400); }
    else if (bWon) { setTeamWinner("B"); playSound("win"); if (localOnWinningTeam) playVoice("win", 1400); }
  }, [game.rankings, teamMode, teamWinner, PLAYERS]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-clear safe cell lightning effect after animation completes
  useEffect(() => {
    if (!safeCellEffect) return;
    const t = setTimeout(() => setSafeCellEffect(null), 950);
    return () => clearTimeout(t);
  }, [safeCellEffect]);

  // Compute meme stats once when game ends, show card after 2.5s
  useEffect(() => {
    if (game.winner === null && !teamWinner) return;
    if (memeComputedRef.current) return;
    memeComputedRef.current = true;

    const captures = captureLogRef.current;
    const eatCount = {};
    const eatenCount = {};
    captures.forEach(({ attacker, victim }) => {
      eatCount[attacker]   = (eatCount[attacker]   || 0) + 1;
      eatenCount[victim]   = (eatenCount[victim]    || 0) + 1;
    });
    const villainEntry  = Object.entries(eatCount).sort((a, b) => b[1] - a[1])[0];
    const betrayedEntry = Object.entries(eatenCount).sort((a, b) => b[1] - a[1])[0];

    const elapsed = Date.now() - gameStartTimeRef.current;
    const mins = Math.floor(elapsed / 60000);
    const secs = Math.floor((elapsed % 60000) / 1000);

    setMemeStats({
      winner: game.winner ?? (teamWinner === "A" ? 0 : 1),
      biggestVillain:  villainEntry  ? { playerId: Number(villainEntry[0]),  count: villainEntry[1]  } : null,
      mostBetrayed:    betrayedEntry ? { playerId: Number(betrayedEntry[0]), count: betrayedEntry[1] } : null,
      legendaryPlayer: legendaryPlayerRef.current,
      gameTime:        `${mins}m ${String(secs).padStart(2, "0")}s`,
      totalCaptures:   captures.length,
    });

    const t = setTimeout(() => setShowMemeCard(true), 2500);
    return () => clearTimeout(t);
  }, [game.winner, teamWinner]); // eslint-disable-line react-hooks/exhaustive-deps

  // Emit points to parent whenever they change
  useEffect(() => {
    onPointsChange?.(playerPoints);
  }, [playerPoints, onPointsChange]);

  // Team mode: floating emoji reactions
  const sendReaction = useCallback((emoji) => {
    const id = Date.now() + Math.random();
    const x = 15 + Math.random() * 70;
    setReactions((prev) => [...prev, { id, emoji, x }]);
    setTimeout(() => setReactions((prev) => prev.filter((r) => r.id !== id)), 2200);
  }, []);

  // Last-stand: local player's single board goti while others are still at home
  const lastStandGotiId = useMemo(() => {
    const currentLocalId = online || enableBots ? localPlayerId : null;
    if (currentLocalId === null) return null;
    const localGotis = game.gotis.filter((g) => g.playerId === currentLocalId);
    const onBoard = localGotis.filter((g) => !g.finished && g.position >= 0);
    const atHome  = localGotis.filter((g) => !g.finished && g.position === -1);
    if (onBoard.length === 1 && atHome.length > 0) return onBoard[0].id;
    return null;
  }, [game.gotis, localPlayerId, online, enableBots]);

  // Team mode: teammate's single endangered goti (gets 🛡️ badge)
  const teamLastStandGotiId = useMemo(() => {
    if (!teamMode || !enableBots || localPlayerId === null) return null;
    const teammateId = (localPlayerId + 2) % 4;
    const teammateGotis = game.gotis.filter((g) => g.playerId === teammateId);
    const onBoard = teammateGotis.filter((g) => !g.finished && g.position >= 0);
    const atHome  = teammateGotis.filter((g) => !g.finished && g.position === -1);
    if (onBoard.length === 1 && atHome.length > 0) return onBoard[0].id;
    return null;
  }, [game.gotis, localPlayerId, teamMode, enableBots]);

  return (
    <div className="boardShell">
      <svg
        className="ludoBoardSvg"
        viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
        role="img"
        aria-label="Ludo board"
      >
        <LudoBoxes
          boxes={boxes}
          numberWiseColor={numberWiseColor}
          playerCount={playerCount}
        />

        <LudoPolygon
          polygonData={polygonData}
          polygonDataSmall={polygonDataSmall}
        />

        <LudoTrianglePlayerBox
          numberWiseColor={numberWiseColor}
          triangleCoordsStringArray={triangleCoordsStringArray}
          circleCoordinates={circleCoordinates}
          triangleInnerCordsArray={triangleInnerCordsArray}
          isActive={game.dice ? null : game.currentPlayer}
        />

        <LudoWinBox
          winBoxCordLine={winBoxCordLine}
          numberWiseColor={numberWiseColor}
          playerCount={playerCount}
        />

        <LudoStarBoxes
          numberWiseColor={numberWiseColor}
          playerCount={playerCount}
          boxes={boxes}
          gotis={game.gotis}
        />

        <LudoMarkerGoti
          winBoxCordLine={winBoxCordLine}
          gotis={game.gotis}
          markers={markers}
          homeMarkers={homeMarkers}
          circleCoordinates={circleCoordinates}
          moveRequest={moveRequest}
          onMoveComplete={onMoveComplete}
          numberWiseColor={numberWiseColor}
          currentPlayer={game.currentPlayer}
          localPlayerId={
            online || enableBots ? localPlayerId : game.currentPlayer
          }
          handleAnimation={handleAnimation}
          PLAYERS={PLAYERS}
          movableGotis={movableGotis}
          eatHints={eatHints}
          captureRequest={captureRequest}
          onCaptureComplete={onCaptureComplete}
          finishRequest={finishRequest}
          onFinishComplete={onFinishComplete}
          lastStandGotiId={lastStandGotiId}
          teamLastStandGotiId={teamLastStandGotiId}
          safeCellEffect={safeCellEffect}
          rankings={game.rankings}
        />

        {/* ── +50 finish bonus toasts ── */}
        {finishBonuses.map(({ id, playerId }) => (
          <text
            key={id}
            x={SVG_SIZE / 2}
            y={SVG_SIZE / 2}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize="34"
            fontWeight="900"
            fill={numberWiseColor[playerId]}
            stroke="#000"
            strokeWidth="3"
            paintOrder="stroke"
            style={{ pointerEvents: "none", fontFamily: "Inter, sans-serif" }}
            className="finishBonusText"
          >
            +50
          </text>
        ))}
      </svg>

      {/* ---------- DICE---------- */}
      <React.Suspense fallback={<div style={{ width: 120, height: 120 }} />}>
        <Dice3D
          value={diceDisplayValue}
          rolling={diceRolling}
          color={numberWiseColor[game.currentPlayer]}
          disabled={diceButtonDisabled}
          winner={game.winner}
          onClick={handleDiceClick}
        />
      </React.Suspense>

      {/* ---- SPEED TIMER ---- */}
      {speedMode && !teamMode && isHumanTurn() && game.winner === null && (
        <SpeedTimer timeLeft={timeLeft} />
      )}

      {/* ---- TEAM SCORE BAR ---- */}
      {teamMode && <TeamScore gotis={game.gotis} />}

      {/* ---- TEAM REACTION BUTTONS ---- */}
      {teamMode && !teamWinner && game.winner === null && (
        <div className="teamReactionBar">
          {TEAM_REACTION_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              className="teamReactionBtn"
              onClick={() => sendReaction(emoji)}
              aria-label={emoji}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}

      {/* ---- FLOATING REACTIONS ---- */}
      {reactions.map((r) => (
        <div
          key={r.id}
          className="floatingReaction"
          style={{ left: `${r.x}%` }}
        >
          {r.emoji}
        </div>
      ))}

      {/* ---- TEAM WIN OVERLAY ---- */}
      {teamMode && teamWinner && (
        <div className="teamWinOverlay">
          <div className="teamWinCard">
            <div className="teamWinTrophy">🏆</div>
            <div className="teamWinTitle">Team {teamWinner} Wins!</div>
            <div className="teamWinSub">
              {teamWinner === "A" ? "P1 & P3" : "P2 & P4"} — Amazing teamwork! 🎉
            </div>
            <button
              className="teamWinShareBtn"
              onClick={() => {
                const msg = `We won as Team ${teamWinner} in Ludo Mania! 🏆 Play at ${window.location.origin}`;
                navigator.clipboard.writeText(msg).catch(() => {});
              }}
            >
              📤 Share Victory
            </button>
          </div>
        </div>
      )}

      {/* ---- VOICE TOGGLE ---- */}
      <button
        className="voiceToggleBtn"
        title={voiceEnabled ? "Mute voice reactions" : "Enable voice reactions"}
        aria-label={voiceEnabled ? "Mute voice reactions" : "Enable voice reactions"}
        onClick={() => {
          const next = !voiceEnabled;
          setVoiceEnabled(next);
          setVoiceReactionsEnabled(next);
        }}
      >
        {voiceEnabled ? "🎙️" : "🔇"}
      </button>

      {/* ---- LEGENDARY ROLL OVERLAY ---- */}
      {showLegendary && (
        <div className="legendaryOverlay" aria-live="assertive">
          <div className="legendaryCard">
            <div className="legendaryDice">🎲🎲🎲</div>
            <div className="legendaryText">LEGENDARY ROLL</div>
            <div className="legendarySub">Three sixes in a row! 🔥</div>
          </div>
        </div>
      )}

      {/* ---- RANKING ANNOUNCEMENT POPUP ---- */}
      {rankingAnnouncement && (() => {
        const { playerId, rank } = rankingAnnouncement;
        const ordinals = ["1st", "2nd", "3rd", "4th"];
        const color = numberWiseColor[playerId];
        return (
          <div className="rankingPopupOverlay">
            <div className="rankingPopupCard" style={{ borderColor: color }}>
              <div className="rankingPopupPlayer" style={{ color }}>
                Player {playerId + 1}
              </div>
              <div className="rankingPopupRank" style={{ color }}>
                {ordinals[rank - 1] ?? `${rank}th`}
              </div>
              <div className="rankingPopupPlace">Place!</div>
            </div>
          </div>
        );
      })()}

      {/* ---- SPECTATOR PANEL ---- */}
      <SpectatorPanel
        count={spectatorCount}
        isSpectator={spectatorMode}
        onReact={(emoji) => onSpectatorReaction?.(emoji)}
      />

      {/* ---- SPECTATOR FLOATING REACTIONS ---- */}
      {spectatorReactions.map((r) => (
        <div key={r.id} className="floatingReaction" style={{ left: `${r.x}%` }}>
          {r.emoji}
        </div>
      ))}

      {/* ---- MEME CARD ---- */}
      {!spectatorMode && showMemeCard && memeStats && (
        <MemeCard
          stats={memeStats}
          playerNames={playerNames.length ? playerNames : PLAYERS.map((_, i) => `P${i + 1}`)}
          playerColors={numberWiseColor}
          teamWinner={teamMode ? teamWinner : null}
          onPlayAgain={onPlayAgain}
          onExitHome={onExitHome}
        />
      )}

      {/* ---- DEV PANEL (development only) ---- */}
      {process.env.NODE_ENV === "development" && (
        <DevPanel
          setGame={setGame}
          PLAYERS={PLAYERS}
          TOTAL_CELLS={TOTAL_CELLS}
          setMemeStats={setMemeStats}
          setShowMemeCard={setShowMemeCard}
        />
      )}
    </div>
  );
}

/* ---- DEV PANEL COMPONENT ---- */
function DevPanel({ setGame, PLAYERS, TOTAL_CELLS, setMemeStats, setShowMemeCard }) {
  // Cells where eating is NOT allowed
  const safeCells = new Set(
    PLAYERS.flatMap((p) => [
      (p.start - 1 + TOTAL_CELLS) % TOTAL_CELLS,
      (p.start + 7) % TOTAL_CELLS,
    ]),
  );

  // Cells a player must NOT be placed at to avoid triggering home-column early
  function homeDanger(player) {
    const s = new Set();
    for (let d = 1; d <= 12; d++) {
      const pos = player.start - d;
      if (pos >= 0) s.add(pos);
    }
    return s;
  }

  // First position that passes the filter, searching forward from `base`
  function findPos(base, filter) {
    for (let i = 0; i < TOTAL_CELLS; i++) {
      const pos = (base + i) % TOTAL_CELLS;
      if (filter(pos)) return pos;
    }
    return base % TOTAL_CELLS;
  }

  // ── Scatter: spread every goti across the board without violating rules ──
  function doScatter() {
    setGame((prev) => ({
      ...prev,
      dice: null,
      gotis: prev.gotis.map((g) => {
        const player = PLAYERS.find((p) => p.id === g.playerId);
        const danger = homeDanger(player);
        const gotiIdx = Number(String(g.id).split("-")[1]);
        // Space each player's gotis ~¼ board apart, then offset by goti index
        const sector = Math.floor((TOTAL_CELLS / PLAYERS.length) * player.id);
        const base   = (sector + gotiIdx * 5 + 3) % TOTAL_CELLS;
        const pos    = findPos(base, (p) => !danger.has(p));
        return { ...g, position: pos, finished: false, finishOrder: undefined };
      }),
    }));
  }

  // ── Eating setup: attacker 1 step behind a chain of enemies ──
  function doEatingSetup() {
    setGame((prev) => {
      const cp       = prev.currentPlayer;
      const cpPlayer = PLAYERS.find((p) => p.id === cp);
      const cpDanger = homeDanger(cpPlayer);

      // Attacker position: first non-safe, non-home-danger cell
      const attackerPos = findPos(4, (p) => !safeCells.has(p) && !cpDanger.has(p));

      // Enemies line up one step ahead and beyond (non-safe cells)
      let nextTarget = (attackerPos + 1) % TOTAL_CELLS;

      return {
        ...prev,
        dice: 1,
        gotis: prev.gotis.map((g) => {
          if (g.playerId === cp) {
            // All current player's gotis stack on attackerPos (only top one matters)
            return { ...g, position: attackerPos, finished: false };
          }
          // Each enemy placed one cell further, skipping safe cells
          const targetPos = findPos(nextTarget, (p) => !safeCells.has(p));
          nextTarget = (targetPos + 1) % TOTAL_CELLS;
          return { ...g, position: targetPos, finished: false };
        }),
      };
    });
  }

  // ── Finish setup: current player's goti 2 steps from winning ──
  function doFinishSetup() {
    setGame((prev) => {
      const cp     = prev.currentPlayer;
      const player = PLAYERS.find((p) => p.id === cp);
      const goti   = prev.gotis.find((g) => g.playerId === cp && !g.finished);
      if (!goti) return prev;
      // player.start - 4 + dice(2) = player.start - 2 = finished
      return {
        ...prev,
        dice: 2,
        gotis: prev.gotis.map((g) =>
          g.id === goti.id
            ? { ...g, position: player.start - 4, finished: false, finishOrder: undefined }
            : g,
        ),
      };
    });
  }

  return (
    <div style={{
      position: "fixed", bottom: 12, right: 12, zIndex: 9999,
      display: "flex", flexDirection: "column", gap: 5,
    }}>
      <span style={{ color: "#fff", fontSize: 10, opacity: 0.6, textAlign: "right" }}>DEV</span>

      <button onClick={doScatter}      style={devBtnStyle("#2980b9")}>⬛ Scatter Board</button>
      <button onClick={doEatingSetup}  style={devBtnStyle("#c0392b")}>⚔ Setup Eating</button>
      <button onClick={doFinishSetup}  style={devBtnStyle("#8e44ad")}>🏠 Setup Finish</button>
      <button onClick={() => {
        setMemeStats({
          winner: 0,
          biggestVillain:  { playerId: 2, count: 5 },
          mostBetrayed:    { playerId: 1, count: 3 },
          legendaryPlayer: 3,
          gameTime:        "12m 34s",
          totalCaptures:   9,
        });
        setShowMemeCard(true);
      }} style={devBtnStyle("#16a34a")}>🃏 Test MemeCard</button>
    </div>
  );
}

const devBtnStyle = (bg) => ({
  background: bg, color: "#fff", border: "none",
  borderRadius: 6, padding: "6px 12px", cursor: "pointer",
  fontSize: 12, fontWeight: "bold", opacity: 0.9,
});

export default React.memo(LudoBoard);
